import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";

const ALL_CATEGORIES = ["Buyer Network", "Client", "Prospects", "COI", "Capital Providers"] as const;
export type CompanyCategory = typeof ALL_CATEGORIES[number];

const CATEGORY_ROUTES: Record<CompanyCategory, string> = {
  "Buyer Network": "buyers",
  "Client": "clients",
  "Prospects": "prospects",
  "COI": "companies",
  "Capital Providers": "capital-providers",
};

const MA_CATEGORIES: CompanyCategory[] = ["Buyer Network", "Capital Providers"];

export interface MoveToCategoryCompany {
  id: string;
  name: string;
  category: string;
  dualRoleBuyerCp?: boolean | null;
}

/**
 * Shared logic for moving a company between categories and toggling the
 * Buyer Network / Capital Providers dual-role flag. Used by every entity
 * detail page that shows a "Move to" dropdown.
 */
export function useMoveToCategory(company: MoveToCategoryCompany) {
  const queryClient = useQueryClient();

  const isDualListed = Boolean(company.dualRoleBuyerCp) &&
    (company.category === "Buyer Network" || company.category === "Capital Providers");

  const counterpartCategory: CompanyCategory | null =
    company.category === "Buyer Network" ? "Capital Providers" :
    company.category === "Capital Providers" ? "Buyer Network" : null;

  // Categories shown in the "Move to" dropdown, excluding the current primary.
  // When dual-listed, we ALSO exclude the counterpart because that slot is
  // replaced by a "Remove from {counterpart}" item instead.
  const otherCategories = ALL_CATEGORIES.filter(c => {
    if (c === company.category) return false;
    if (isDualListed && c === counterpartCategory) return false;
    return true;
  });

  const invalidateListCaches = () => {
    queryClient.invalidateQueries({ queryKey: ["buyers"] });
    queryClient.invalidateQueries({ queryKey: ["capital-providers"] });
    queryClient.invalidateQueries({ queryKey: ["clients"] });
    queryClient.invalidateQueries({ queryKey: ["cois"] });
    queryClient.invalidateQueries({ queryKey: ["/api/prospects"] });
    queryClient.invalidateQueries({ queryKey: ["company", company.id] });
  };

  /**
   * Exclusive migration to a new category. Performs the PATCH, toasts,
   * and hard-reloads to the target category's route.
   *
   * NOTE: this function no longer shows a confirmation dialog. Callers
   * are responsible for gating the call behind their own UI
   * (MoveToDropdown uses a shadcn ConfirmDialog). The old implementation
   * called the browser-native window.confirm() here which was ugly,
   * keyboard-hostile, and inconsistent with every other confirmation UX
   * in the CRM. See 43191a0 for the same class of cleanup on
   * CompanyDetail.
   */
  const moveToCategory = async (target: CompanyCategory) => {
    const willDropDualListing = isDualListed && counterpartCategory &&
      target !== counterpartCategory && target !== company.category;

    // Prospect -> Client is a conversion, not a category edit: it keeps the
    // origination, clears the financials star and logs the change. The
    // generic PATCH refuses this transition with a 409.
    if (target === "Client" && company.category === "Prospects") {
      const r = await fetch(`/api/prospects/convert/${company.id}`, {
        method: "POST", credentials: "include", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({}),
      });
      if (!r.ok) { toast.error("Failed to convert"); return; }
      toast.success("Converted to Client");
      window.location.href = `/clients/${company.id}?promptProject=true`;
      return;
    }

    const res = await fetch(`/api/companies/${company.id}`, {
      method: "PATCH",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ category: target }),
    });
    if (!res.ok) {
      toast.error("Failed to move company");
      return;
    }

    const toastMessage = willDropDualListing
      ? `Moved to ${target} (removed from ${counterpartCategory})`
      : `Moved to ${target}`;
    toast.success(toastMessage);

    // Hard reload. Unmounts current detail component, mounts target's
    // dedicated component with fresh React Query caches.
    window.location.href = `/${CATEGORY_ROUTES[target]}/${company.id}`;
  };

  /**
   * Add dual-role listing. Only valid when the current category is
   * Buyer Network or Capital Providers — the caller is responsible
   * for only exposing this action in those states.
   */
  const addDualRole = async () => {
    if (!MA_CATEGORIES.includes(company.category as CompanyCategory)) {
      toast.error("Dual role is only available for Buyer Network or Capital Providers");
      return;
    }
    const res = await fetch(`/api/companies/${company.id}`, {
      method: "PATCH",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ dualRoleBuyerCp: true }),
    });
    if (!res.ok) {
      toast.error("Failed to update dual role");
      return;
    }
    toast.success(`Now listed in both Buyer Network and Capital Providers`);
    invalidateListCaches();
  };

  /**
   * Remove the dual-role listing. The company stays on its current page
   * (primary category is unchanged). The OTHER list loses this company.
   */
  const removeDualRole = async () => {
    const res = await fetch(`/api/companies/${company.id}`, {
      method: "PATCH",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ dualRoleBuyerCp: false }),
    });
    if (!res.ok) {
      toast.error("Failed to update dual role");
      return;
    }
    toast.success(`Removed from ${counterpartCategory} list`);
    invalidateListCaches();
  };

  return {
    moveToCategory,
    addDualRole,
    removeDualRole,
    otherCategories,
    isDualListed,
    counterpartCategory,
  };
}
